import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Sparkles, Edit, Trash2, CheckCircle, XCircle, Clock, ShieldCheck, GraduationCap } from "lucide-react";
import { ApiService } from "../../services/api";
import checkPermission, { checkSuperAdmin } from "../../utils/permission";
import { useModal } from "../../hooks/useModal";
import { IlmekContentPack, IlmekContentPackStatus } from "../../types/ilmek";

const STATUS_BADGE: Record<IlmekContentPackStatus, { label: string; className: string }> = {
  TASLAK: { label: "Taslak", className: "bg-gray-100 text-gray-700 border-gray-200" },
  ONAY_BEKLIYOR: { label: "Onay Bekliyor", className: "bg-amber-50 text-amber-700 border-amber-200" },
  ONAYLANDI: { label: "Onaylandı", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  REDDEDILDI: { label: "Reddedildi", className: "bg-rose-50 text-rose-700 border-rose-200" },
};

export default function IlmekContentPackListPage() {
  const navigate = useNavigate();
  const { showAlert } = useModal();

  const [packs, setPacks] = useState<IlmekContentPack[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<string>("");

  const isAdmin = checkSuperAdmin();
  const canAdd = isAdmin || checkPermission("ilmek_content_pack_add");
  const canUpdate = isAdmin || checkPermission("ilmek_content_pack_update");
  const canDelete = isAdmin || checkPermission("ilmek_content_pack_delete");
  const canApprove = isAdmin || checkPermission("ilmek_content_pack_approve");

  const loadPacks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ApiService.getIlmekContentPacks({
        search: search || undefined,
        limit: 100,
      });
      setPacks(Array.isArray(res?.data) ? res.data : []);
    } catch (error: any) {
      await showAlert({
        title: "Hata",
        message: error?.message || "İçerik paketleri yüklenemedi.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [search, showAlert]);

  useEffect(() => {
    loadPacks();
  }, [loadPacks]);

  const handleApprove = useCallback(
    async (pack: IlmekContentPack, action: "APPROVE" | "REJECT") => {
      try {
        await ApiService.approveIlmekContentPack(pack.id, action);
        await showAlert({
          title: "Başarılı",
          message: action === "APPROVE" ? "Paket onaylandı." : "Paket reddedildi.",
          type: "success",
        });
        loadPacks();
      } catch (error: any) {
        await showAlert({
          title: "Hata",
          message: error?.message || "İşlem tamamlanamadı.",
          type: "error",
        });
      }
    },
    [showAlert, loadPacks]
  );

  const handleDelete = useCallback(
    async (pack: IlmekContentPack) => {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`"${pack.baslik || pack.kazanimKodu}" paketini silmek istediğinize emin misiniz?`)) {
        return;
      }
      try {
        await ApiService.deleteIlmekContentPack(pack.id);
        loadPacks();
      } catch (error: any) {
        await showAlert({
          title: "Hata",
          message: error?.message || "Paket silinemedi.",
          type: "error",
        });
      }
    },
    [showAlert, loadPacks]
  );

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            İLMEK — Kazanım İçerik Paketleri
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-300 mt-1">
            "Doğruluk insanda, anlatım yapay zekâda." — Öğrenciye yalnızca öğretmen onaylı paket sunulur.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => navigate("/ilmek/ogren")}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white border border-purple-200 text-purple-700 text-sm font-medium hover:bg-purple-50"
          >
            <GraduationCap className="w-4 h-4" />
            Öğrenci olarak dene
          </button>
          {canAdd && (
            <>
              <button
                onClick={() => navigate("/ilmek/new")}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700"
              >
                <Plus className="w-4 h-4" />
                Yeni Paket
              </button>
              <button
                onClick={() => navigate("/ilmek/new?mode=generate")}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white border border-purple-200 text-purple-700 text-sm font-medium hover:bg-purple-50"
              >
                <Sparkles className="w-4 h-4" />
                AI ile Üret
              </button>
            </>
          )}
        </div>
      </div>

      <div className="mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Kazanım kodu, başlık veya ünite ara..."
          className="w-full sm:w-96 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-200"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
        </div>
      ) : packs.length === 0 ? (
        <div className="text-center py-16 text-gray-500 dark:text-gray-400">
          <p className="text-sm">Henüz içerik paketi yok. "Yeni Paket" ile başlayın.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {packs.map((pack) => {
            const badge = STATUS_BADGE[pack.onayDurumu] || STATUS_BADGE.TASLAK;
            const finalized = pack.onayDurumu === "ONAYLANDI" || pack.onayDurumu === "REDDEDILDI";
            return (
              <div key={pack.id} className="border border-gray-200 rounded-lg p-4 bg-white hover:shadow-md transition-shadow flex flex-col">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <span className="text-xs font-mono text-purple-600 bg-purple-50 px-2 py-0.5 rounded">
                    {pack.kazanimKodu}
                  </span>
                  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${badge.className}`}>
                    {badge.label}
                  </span>
                </div>
                <h3 className="text-base font-semibold text-gray-900 break-words mb-1">
                  {pack.baslik || "(başlıksız)"}
                </h3>
                <p className="text-xs text-gray-500 mb-3">
                  {[pack.ders, pack.sinif ? `${pack.sinif}. sınıf` : null, pack.unite]
                    .filter(Boolean)
                    .join(" · ")}
                  {pack.surum ? ` · v${pack.surum}` : ""}
                </p>

                <div className="mt-auto pt-3 border-t border-gray-100 flex flex-wrap gap-2">
                  {canUpdate && (
                    <button
                      onClick={() => navigate(`/ilmek/edit/${pack.id}`)}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100"
                    >
                      <Edit className="w-3.5 h-3.5" /> Düzenle
                    </button>
                  )}
                  {canApprove && !finalized && (
                    <>
                      <button
                        onClick={() => handleApprove(pack, "APPROVE")}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
                      >
                        <CheckCircle className="w-3.5 h-3.5" /> Onayla
                      </button>
                      <button
                        onClick={() => handleApprove(pack, "REJECT")}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium text-rose-700 bg-rose-50 hover:bg-rose-100"
                      >
                        <XCircle className="w-3.5 h-3.5" /> Reddet
                      </button>
                    </>
                  )}
                  {canApprove && finalized && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-400">
                      <Clock className="w-3.5 h-3.5" /> Sonuçlandı
                    </span>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => handleDelete(pack)}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Sil
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
